// Enums
export enum ComplaintStatus {
  WAITING_CLIENT = 'waiting_client',
  WAITING_CUSTOMER_SERVICE = 'waiting_customer_service',
  WAITING_VALUATION = 'waiting_valuation',
  WAITING_COMPLAINT_HANDLER = 'waiting_complaint_handler',
  WAITING_CC_MANAGER = 'waiting_cc_manager',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
}

export enum ComplaintFiledBy {
  CLIENT = 'client',
  THIRD_PARTY = 'third party',
  INTERNAL = 'internal',
  OTHER = 'other',
}

export enum ComplaintCategory {
  BUYER = 'Buyer',
  PACKAGER = 'Packager',
  CUSTOMER_SERVICE = 'CustomerService',
  VALUATION = 'Valuation',
  TELLER = 'Teller',
  BURSAR = 'Bursar',
  LOGISTICS = 'Logistics',
  OTHER = 'Other',
}

export enum ComplaintSubCategory {
  // Buyer related
  BUYERS_BEHAVIOR = 'buyers_behavior',
  WRONG_MATERIALS_BUYER = 'wrong_materials_buyer',
  WRONG_PROPOSITION = 'wrong_proposition',
  LACK_IN_SPEECH = 'lack_in_speech',
  OTHER_BUYER = 'other_buyer',

  // Packager related
  WRONG_MATERIALS_PACKAGER = 'wrong_materials_packager',
  LACKING_MATERIALS = 'lacking_materials',
  OTHER_PACKAGER = 'other_packager',

  // Customer service related
  CUSTOMER_SERVICE_BEHAVIOR = 'customer_service_behavior',
  OTHER_CUSTOMER_SERVICE = 'other_customer_service',

  // Valuation related
  WRONG_VALUATION = 'wrong_valuation',
  OTHER_VALUATION = 'other_valuation',

  // Teller related
  TELLERS_BEHAVIOR = 'tellers_behavior',
  WRONG_INFOS = 'wrong_infos',
  LOW_PRICE = 'low_price',
  OTHER_TELLER = 'other_teller',

  // Bursar related
  WRONG_NO_RETURN = 'wrong_no_return',
  OTHER_BURSAR = 'other_bursar',

  // Logistics related
  PACKAGE_MISSING_INCOMING_MH = 'package_missing_incoming_mh',
  PACKAGE_MISSING_OUTGOING_POSTI = 'package_missing_outgoing_posti',
  PACKAGE_MISSING_OUTGOING_MH = 'package_missing_outgoing_mh',
  OTHER_LOGISTICS = 'other_logistics',

  // Other
  OTHER_OTHER = 'other_other',
}

export type ComplaintOrderStatus = 'open' | 'closed' | 'not_set';

export enum CloseMethod {
  COMPLAINT = 'complaint',
  MANUAL = 'manual',
  SYSTEM = 'system',
}

// Namespace for API types
export declare namespace ComplaintAPI {
  // Interfaces
  export interface ComplaintDateRange {
    start?: string;
    end?: string;
  }

  export interface ComplaintFilters {
    complaintNumber?: string;
    orderNumber?: string;
    clientNumber?: string;
    complaintDateRange?: ComplaintDateRange;
    dueDateRange?: ComplaintDateRange;
    reasonCategory?: ComplaintCategory;
    reasonSubcategory?: ComplaintSubCategory;
    status?: ComplaintStatus;
    isClosed?: boolean;
    phoneNumber?: string;
    email?: string;
    complaintFiledBy?: ComplaintFiledBy;
    overdue?: boolean;
    limit?: number;
    skip?: number;
  }

  export interface ComplaintClientInfo {
    id: string;
    clientNumber: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  }

  export interface ComplaintOrderInfo {
    id: string;
    orderNumber: string;
    createdAt: string;
    closed: boolean;
  }

  export interface ComplaintMemberInfo {
    id: string;
    username: string;
    email: string;
    name: string;
  }

  export interface Complaint {
    id: string;
    complaintNumber: string;
    complaintDate: string | Date;
    dueDate: string | Date;
    reasonCategory: ComplaintCategory;
    reasonSubcategory: ComplaintSubCategory;
    logisticsLastSeenLocation?: string;
    logisticsLastSeenDate?: string | Date;
    reasonOtherText?: string;
    complaintInfo: string;
    complaintFiledBy: ComplaintFiledBy;
    clientId?: string;
    orderId?: string;
    memberId: string;
    status: ComplaintStatus;
    isClosed: boolean;
    client?: ComplaintClientInfo;
    order?: ComplaintOrderInfo;
    member?: ComplaintMemberInfo;
    orderClosed?: ComplaintOrderStatus;
  }

  export interface ComplaintPaginatedResponse<T> {
    data: T[];
    total: number;
    limit: number;
    skip: number;
  }

  export interface ComplaintResponse extends ComplaintPaginatedResponse<Complaint> {}

  export interface ComplaintCloseOrderData {
    complaintId: string;
    closeMethod?: CloseMethod;
  }

  export interface ComplaintCreateData {
    dueDate: string | Date;
    reasonCategory: ComplaintCategory;
    reasonSubcategory: ComplaintSubCategory;
    complaintDate: string | Date;
    complaintInfo: string;
    complaintFiledBy: ComplaintFiledBy;
    clientNumber?: number | null;
    orderNumber?: number | null;
    memberId?: string;
    status: ComplaintStatus;
  }

  export interface ComplaintUpdateData {
    dueDate: string | Date;
    reasonCategory: ComplaintCategory;
    reasonSubcategory: ComplaintSubCategory;
    reasonOtherText?: string;
    logisticsLastSeenLocation?: string;
    logisticsLastSeenDate?: string | Date;
    complaintDate: string | Date;
    complaintInfo: string;
    complaintFiledBy: ComplaintFiledBy;
    status: ComplaintStatus;
    isClosed: boolean;
  }

  // API Response Types
  export type ListComplaintsResponse = ComplaintResponse;
  export type GetComplaintResponse = { complaint: Complaint };
  export type CreateComplaintResponse = { complaint: Complaint };
  export type UpdateComplaintResponse = { count: number };
  export type CloseComplaintResponse = Complaint;
  export type CloseOrderResponse = ComplaintOrderInfo;
  export type ValidateClientResponse = boolean;
  export type ValidateOrderResponse = boolean;
}
